import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, ExternalLink } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Courses', path: '/courses' },
    { name: 'Testimonials', path: '/testimonials' },
    { name: 'Notes', path: '/notes' },
    { name: 'Contact', path: '/contact' },
    { name: 'Verify Certificate', path: '/verify' },
    { name: 'Student Syllabus Tracker', path: 'https://student.geniusacademyklbg.com/student', external: true },
  ];

  const isActive = (path) => location.pathname === path;

  const linkBase =
    "relative font-medium text-[13px] xl:text-sm tracking-tight whitespace-nowrap transition-colors duration-200 py-1.5 after:content-[''] after:absolute after:left-0 after:-bottom-0.5 after:h-[2px] after:w-full after:origin-left after:scale-x-0 after:bg-cyan-600 after:transition-transform after:duration-300";

  return (
    <nav className="fixed top-0 w-full z-50 bg-white/85 backdrop-blur-md border-b border-slate-100 shadow-[0_1px_0_0_rgba(15,23,42,0.02)]">
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 md:h-20 gap-4">
          <Link to="/" className="flex items-center gap-2 md:gap-3 flex-shrink-0" data-testid="navbar-logo-link">
            <img
              src="https://customer-assets.emergentagent.com/job_33cef519-6a19-4ccb-ace7-ff1a08bb6e04/artifacts/209s83l6_11%20%285%29.png"
              alt="Genius Academy Logo"
              className="h-10 md:h-12 lg:h-14"
            />
            <div className="leading-tight">
              <span className="block text-sm md:text-base xl:text-lg font-bold text-slate-900 tracking-tight">Genius Academy</span>
              <p className="hidden sm:block text-[10px] md:text-[11px] text-slate-500">Technical &amp; Computer Training Institute</p>
            </div>
          </Link>

          {/* Desktop Navigation (≥1280px) */}
          <div className="hidden xl:flex items-center gap-6 2xl:gap-8">
            {navLinks.map((link) => {
              const active = !link.external && isActive(link.path);
              const cls = `${linkBase} ${
                active
                  ? 'text-cyan-600 after:scale-x-100'
                  : 'text-slate-700 hover:text-cyan-600 hover:after:scale-x-100'
              }`;
              const testId = `nav-link-${link.name.toLowerCase().replace(/ /g, '-')}`;

              if (link.external) {
                return (
                  <a
                    key={link.path}
                    href={link.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`${cls} inline-flex items-center gap-1`}
                    data-testid={testId}
                  >
                    {link.name}
                    <ExternalLink className="w-3 h-3 opacity-60" strokeWidth={2.5} />
                  </a>
                );
              }
              return (
                <Link key={link.path} to={link.path} className={cls} data-testid={testId}>
                  {link.name}
                </Link>
              );
            })}
            <a
              href="tel:9164998997"
              className="flex items-center gap-2 whitespace-nowrap bg-orange-500 hover:bg-orange-600 text-white px-5 py-2.5 rounded-full font-bold text-sm shadow-md hover:shadow-lg transition-all transform hover:-translate-y-0.5"
              data-testid="nav-call-button"
            >
              <Phone size={16} />
              Call Now
            </a>
          </div>

          {/* Mobile / Tablet trigger (<1280px) */}
          <div className="xl:hidden flex items-center gap-2">
            <a
              href="tel:9164998997"
              className="hidden sm:inline-flex items-center gap-2 whitespace-nowrap bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-full font-bold text-xs shadow-md transition-all"
              data-testid="nav-call-button-compact"
            >
              <Phone size={14} />
              Call
            </a>
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="p-2 rounded-lg text-slate-700 hover:bg-slate-100 transition-colors"
              data-testid="mobile-menu-button"
              aria-label="Toggle menu"
              aria-expanded={isOpen}
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile / Tablet Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="xl:hidden bg-white border-t border-slate-100"
          >
            <div className="px-4 py-4 space-y-1.5">
              {navLinks.map((link) => {
                const testId = `mobile-nav-link-${link.name.toLowerCase().replace(/ /g, '-')}`;
                if (link.external) {
                  return (
                    <a
                      key={link.path}
                      href={link.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setIsOpen(false)}
                      className="flex items-center justify-between py-2.5 px-4 rounded-lg font-medium text-slate-700 hover:bg-slate-50 hover:text-cyan-600 transition-colors"
                      data-testid={testId}
                    >
                      <span>{link.name}</span>
                      <ExternalLink className="w-4 h-4 opacity-50" />
                    </a>
                  );
                }
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setIsOpen(false)}
                    className={`block py-2.5 px-4 rounded-lg font-medium transition-colors ${
                      isActive(link.path)
                        ? 'bg-cyan-50 text-cyan-600'
                        : 'text-slate-700 hover:bg-slate-50'
                    }`}
                    data-testid={testId}
                  >
                    {link.name}
                  </Link>
                );
              })}
              <a
                href="tel:9164998997"
                className="flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-full font-bold transition-colors mt-2"
                data-testid="mobile-call-button"
              >
                <Phone size={18} />
                Call Now
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
